/**
 * Phase 19a — Creative Studio Hero-Image Generator.
 *
 * Fires POST /creative/hero-image/generate synchronously (backend does
 * inline generation). Displays a status pill during the request and
 * transitions to the inline preview once ready. If the response's
 * `is_fallback` field is true, an honest "Placeholder — image API not
 * configured" badge sits atop the preview so users never think a real
 * image was generated when it wasn't.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Loader2, RefreshCw, Download, Image as ImageIcon } from "lucide-react";

const ASPECTS = [
  { v: "16:9", label: "16:9 · Landscape" },
  { v: "1:1",  label: "1:1 · Square" },
  { v: "9:16", label: "9:16 · Portrait" },
];
const STYLES = [
  { v: "",                     label_de: "Ohne Stil", label_en: "No style" },
  { v: "photorealistic",       label_de: "Fotorealistisch", label_en: "Photorealistic" },
  { v: "illustration",         label_de: "Illustration",     label_en: "Illustration" },
  { v: "brand-consistent",     label_de: "Marken-konsistent", label_en: "Brand-consistent" },
];

const T = {
  de: {
    title: "Hero-Bild generieren", prompt: "Prompt", aspect: "Format",
    style: "Stil", generate: "Bild generieren",
    prompt_ph: "Modernes Wuppertaler Reihenhaus, warmes Abendlicht …",
    generating: "Erzeuge Bild…", regen: "Neu generieren",
    download: "Herunterladen", failed: "Generierung fehlgeschlagen",
    fallback: "Platzhalter — Bild-API nicht konfiguriert",
    tips: "Tipps: Subjekt · Ort · Stimmung · Komposition",
  },
  en: {
    title: "Generate hero image", prompt: "Prompt", aspect: "Aspect",
    style: "Style", generate: "Generate image",
    prompt_ph: "Modern Wuppertal townhouse, warm evening light …",
    generating: "Generating image…", regen: "Regenerate",
    download: "Download", failed: "Generation failed",
    fallback: "Placeholder — image API not configured",
    tips: "Tips: subject · setting · mood · composition",
  },
};

export default function HeroImageGenerator({ locale = "de", campaignId }) {
  const t = T[locale] || T.de;
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("16:9");
  const [style, setStyle] = useState("");
  const [status, setStatus] = useState(null);        // null | generating | ready | failed
  const [result, setResult] = useState(null);        // {asset_url, is_fallback, provider}

  const canGen = prompt.trim().length >= 3 && status !== "generating";

  async function generate() {
    setStatus("generating");
    try {
      const r = await api.post("/creative/hero-image/generate", {
        prompt: prompt.trim(), aspect_ratio: aspect,
        style: style || null, campaign_id: campaignId || null,
      });
      setResult(r.data);
      setStatus("ready");
    } catch (e) {
      const msg = e?.response?.data?.detail?.reason
                    || e?.response?.data?.detail?.error
                    || e?.message || t.failed;
      toast.error(msg);
      setStatus("failed");
    }
  }

  const pillColor = status === "ready"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300"
    : status === "failed"
    ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300"
    : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300";

  return (
    <div className="space-y-4" data-testid="hero-image-generator">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-primary" aria-hidden />
        <h3 className="text-sm font-semibold">{t.title}</h3>
        <span className="text-xs text-muted-foreground">· {t.tips}</span>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="md:col-span-3 space-y-1.5">
          <Label htmlFor="hero-prompt">{t.prompt}</Label>
          <Textarea
            id="hero-prompt" data-testid="hero-image-prompt"
            value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder={t.prompt_ph} rows={3}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t.aspect}</Label>
          <Select value={aspect} onValueChange={setAspect}>
            <SelectTrigger data-testid="hero-image-aspect"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ASPECTS.map((a) => (
                <SelectItem key={a.v} value={a.v}
                                data-testid={`hero-image-aspect-opt-${a.v}`}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>{t.style}</Label>
          <Select value={style || "none"}
                      onValueChange={(v) => setStyle(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="hero-image-style"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STYLES.map((s) => (
                <SelectItem key={s.v || "none"} value={s.v || "none"}>
                  {locale === "de" ? s.label_de : s.label_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end gap-2">
          <Button
            data-testid="hero-image-generate-btn"
            disabled={!canGen} onClick={generate}
          >
            {status === "generating"
              ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.generating}</>)
              : t.generate}
          </Button>
        </div>
      </div>

      {status && (
        <div className="flex flex-wrap items-center gap-3">
          <span
            data-testid="hero-image-status-pill"
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${pillColor}`}
          >
            <span className={
              "inline-block size-1.5 rounded-full "
              + (status === "ready" ? "bg-emerald-500"
                  : status === "failed" ? "bg-red-500"
                  : "bg-amber-500 animate-pulse")}
            />
            {status}
          </span>
          {status === "ready" && result?.is_fallback && (
            <span
              data-testid="hero-image-fallback-badge"
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"
            >
              {t.fallback}
            </span>
          )}
        </div>
      )}

      {status === "ready" && result?.asset_url && (
        <div className="space-y-2" data-testid="hero-image-result">
          <img
            src={result.asset_url} alt={prompt}
            data-testid="hero-image-preview"
            style={{ maxWidth: "480px" }}
            className="rounded-md border shadow-sm h-auto"
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm"
                        data-testid="hero-image-regenerate-btn"
                        disabled={!canGen} onClick={generate}>
              <RefreshCw className="mr-2 h-3.5 w-3.5" />{t.regen}
            </Button>
            <a
              href={result.asset_url} download
              data-testid="hero-image-download-link"
              className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
              target="_blank" rel="noreferrer"
            >
              <Download className="mr-2 h-3.5 w-3.5" />{t.download}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
